import { Switch } from '@components/ui/switch'
import { Separator } from '@components/ui/separator'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/ui/tooltip'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { FREEZE_UNAVAILABLE_IN_GROUP_MESSAGE } from '@/lib/editor/freeze-object-messages'
import { isShapeTextFrame } from '@/lib/shapes/shape-text-frame'

interface ProtectionSwitchesProps {
  activeObjects: ShapeData[]
  disabled: boolean
  isInGroupIsolationMode: boolean
  freezeSwitchId: string
  lockSwitchId: string
  ignoreWrapSwitchId: string
  freezeObject: boolean
  lockObject: boolean
  ignoreWrap: boolean
  ignoreWrapMixed: boolean
  onFreezeObjectChange: (checked: boolean) => void
  onLockObjectChange: (checked: boolean) => void
  onIgnoreWrapChange: (checked: boolean) => void
}

/** Freeze Object, Lock Object and Ignore wrap switches. */
export function ProtectionSwitches({
  activeObjects,
  disabled,
  isInGroupIsolationMode,
  freezeSwitchId,
  lockSwitchId,
  ignoreWrapSwitchId,
  freezeObject,
  lockObject,
  ignoreWrap,
  ignoreWrapMixed,
  onFreezeObjectChange,
  onLockObjectChange,
  onIgnoreWrapChange,
}: ProtectionSwitchesProps) {
  const selectionHasAnyLocked = activeObjects.some((s) => s.locked === true)
  const selectionHasAnyFrozen = activeObjects.some((s) => s.frozen === true)

  return (
    <>
      {/* Freeze Object */}
      <div className="flex items-center justify-between">
        <label
          htmlFor={freezeSwitchId}
          className="text-sm font-medium text-foreground leading-none"
        >
          Freeze Object
        </label>
        {isInGroupIsolationMode ? (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex cursor-not-allowed">
                  <Switch
                    id={freezeSwitchId}
                    checked={freezeObject}
                    onCheckedChange={onFreezeObjectChange}
                    disabled
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent side="left">{FREEZE_UNAVAILABLE_IN_GROUP_MESSAGE}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Switch
            id={freezeSwitchId}
            checked={freezeObject}
            onCheckedChange={onFreezeObjectChange}
            disabled={disabled || selectionHasAnyLocked}
          />
        )}
      </div>

      {/* Lock Object */}
      <div className="flex items-center justify-between">
        <label htmlFor={lockSwitchId} className="text-sm font-medium text-foreground leading-none">
          Lock Object
        </label>
        <Switch
          id={lockSwitchId}
          checked={lockObject}
          onCheckedChange={onLockObjectChange}
          disabled={disabled || isInGroupIsolationMode || selectionHasAnyFrozen}
        />
      </div>

      {/* Ignore wrap — always present, like Freeze Object/Lock Object, never removed from the DOM.
          `ignoreTextWrap` is a field any shape can carry on the wire, but the switch is only
          meaningful — and only enabled — for a selection of text frames; a plain shape has no text
          of its own to exempt from nearby wrap sources. */}
      <Separator />

      <div className="flex items-center justify-between">
        <label
          htmlFor={ignoreWrapSwitchId}
          className="text-sm font-medium text-foreground leading-none"
        >
          Ignore wrap
        </label>
        <div className="flex items-center gap-2">
          {ignoreWrapMixed && (
            <span className="text-xs font-medium text-muted-foreground leading-none">Mixed</span>
          )}
          <Switch
            id={ignoreWrapSwitchId}
            // `mixed` renders the true indeterminate visual (thumb centered, neutral track —
            // see switch.tsx); `checked` stays the real underlying value throughout, so a
            // click from mixed resolves to "on" for every selected object, same as an
            // indeterminate checkbox's usual click convention.
            checked={ignoreWrap}
            mixed={ignoreWrapMixed}
            onCheckedChange={onIgnoreWrapChange}
            disabled={
              disabled ||
              isInGroupIsolationMode ||
              (activeObjects.length > 0 && !activeObjects.every(isShapeTextFrame))
            }
            aria-label={ignoreWrapMixed ? 'Ignore wrap (mixed)' : 'Ignore wrap'}
          />
        </div>
      </div>
    </>
  )
}
